import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChevronDown, RectangleHorizontal, RectangleVertical, Square } from 'lucide-react';
import React from 'react';
import { TOOLTIP_CLASSES } from './ControlIcons';

const RATIO_LABELS: Record<string, string> = {
  auto: 'Auto Detect',
  '16:9': 'Widescreen',
  '9:16': 'Portrait · Reels',
  '1:1': 'Square',
  '4:3': 'Standard',
  '3:4': 'Portrait',
  '3:2': 'Landscape',
  '2:3': 'Portrait',
  '5:4': 'Photo Print',
  '4:5': 'Social',
  '21:9': 'Cinematic',
  '8:1': 'Panoramic',
  '4:1': 'Banner',
  '1:4': 'Tall Strip',
  '1:8': 'Ultra Tall',
};

interface AspectRatioSelectorProps {
  model: string;
  aspectRatio: string;
  activeDropdown: string | null;
  toggleDropdown: (name: string) => void;
  handleSelect: (type: string, val: string, label: string) => void;
  highlighted: boolean;
  selectedImage?: string | null;
  selectedImages?: string[];
}

export function AspectRatioSelector({
  model,
  aspectRatio,
  activeDropdown,
  toggleDropdown,
  handleSelect,
  highlighted,
  selectedImage,
  selectedImages,
}: AspectRatioSelectorProps) {
  // If Grok Imagine and an image is provided, hide aspect ratio selector
  if (
    model === 'grok-imagine' &&
    (selectedImage || (selectedImages && selectedImages.length > 0))
  ) {
    return null;
  }

  const tooltipLabel = RATIO_LABELS[aspectRatio]
    ? `${aspectRatio} Ratio · ${RATIO_LABELS[aspectRatio]}`
    : `${aspectRatio} Ratio`;

  // Find current icon for the trigger button
  const CurrentIcon =
    aspectRatio === '16:9' ||
    aspectRatio === '3:2' ||
    aspectRatio === '5:4' ||
    aspectRatio === '21:9' ||
    aspectRatio === '8:1' ||
    aspectRatio === '4:1'
      ? RectangleHorizontal
      : aspectRatio === '9:16' ||
          aspectRatio === '3:4' ||
          aspectRatio === '2:3' ||
          aspectRatio === '4:5' ||
          aspectRatio === '1:4' ||
          aspectRatio === '1:8'
        ? RectangleVertical
        : aspectRatio === '1:1'
          ? Square
          : RectangleHorizontal; // Fallback

  return (
    <div className="control-group">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={cn('custom-select', highlighted && 'pulse-highlight')}
            onClick={() => toggleDropdown('ratio')}
            aria-haspopup="listbox"
            aria-expanded={activeDropdown === 'ratio'}
            aria-label="Select Aspect Ratio"
          >
            <span className="select-icon">
              <CurrentIcon className="h-6 w-6" />
            </span>
            <span className="select-arrow">
              <ChevronDown
                className={`h-3 w-3 transition-transform ${activeDropdown === 'ratio' ? 'rotate-180' : ''}`}
              />
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent showArrow={false} className={TOOLTIP_CLASSES} sideOffset={8}>
          {tooltipLabel}
        </TooltipContent>
      </Tooltip>
      {activeDropdown === 'ratio' && (
        <div className="select-options active">
          {model === 'nano-banana-2' ? (
            <>
              {[{ r: 'auto', Icon: Square, label: 'Auto Detect' }].map(({ r, Icon, label }) => (
                <div
                  key={r}
                  className={cn('option', aspectRatio === r && 'selected')}
                  onClick={() => handleSelect('aspectRatio', r, r)}
                >
                  <Icon className="h-6 w-6 flex-shrink-0" />
                  <span>{r}</span>
                  <span className="ml-auto text-gray-400 text-xs">{label}</span>
                </div>
              ))}
              <div className="dropdown-header mt-1 first:mt-0">Popular</div>
              {[
                { r: '16:9', Icon: RectangleHorizontal, label: 'YouTube' },
                { r: '9:16', Icon: RectangleVertical, label: 'Story / Reel' },
                { r: '1:1', Icon: Square, label: 'Square' },
                { r: '4:3', Icon: RectangleHorizontal, label: 'Standard' },
                { r: '3:4', Icon: RectangleVertical, label: 'Classic Portrait' },
              ].map(({ r, Icon, label }) => (
                <div
                  key={r}
                  className={cn('option', aspectRatio === r && 'selected')}
                  onClick={() => handleSelect('aspectRatio', r, r)}
                >
                  <Icon className="h-6 w-6 flex-shrink-0" />
                  <span>{r}</span>
                  <span className="ml-auto text-gray-400 text-xs">{label}</span>
                </div>
              ))}
              <div className="dropdown-header mt-1">Speciality</div>
              {[
                { r: '3:2', Icon: RectangleHorizontal, label: 'Landscape' },
                { r: '2:3', Icon: RectangleVertical, label: 'Portrait' },
                { r: '5:4', Icon: RectangleHorizontal, label: 'Photo Print' },
                { r: '4:5', Icon: RectangleVertical, label: 'Social Portrait' },
                { r: '21:9', Icon: RectangleHorizontal, label: 'Cinematic' },
                { r: '8:1', Icon: RectangleHorizontal, label: 'Panoramic' },
                { r: '4:1', Icon: RectangleHorizontal, label: 'Wide Banner' },
                { r: '1:4', Icon: RectangleVertical, label: 'Tall Strip' },
                { r: '1:8', Icon: RectangleVertical, label: 'Ultra Tall' },
              ].map(({ r, Icon, label }) => (
                <div
                  key={r}
                  className={cn('option', aspectRatio === r && 'selected')}
                  onClick={() => handleSelect('aspectRatio', r, r)}
                >
                  <Icon className="h-6 w-6 flex-shrink-0" />
                  <span>{r}</span>
                  <span className="ml-auto text-gray-400 text-xs">{label}</span>
                </div>
              ))}
            </>
          ) : model === 'z-image' ? (
            <>
              {[
                { r: '16:9', Icon: RectangleHorizontal, label: 'Widescreen' },
                { r: '9:16', Icon: RectangleVertical, label: 'Story / Reel' },
                { r: '1:1', Icon: Square, label: 'Square' },
                { r: '4:3', Icon: RectangleHorizontal, label: 'Standard' },
                { r: '3:4', Icon: RectangleVertical, label: 'Portrait' },
              ].map(({ r, Icon, label }) => (
                <div
                  key={r}
                  className={cn('option', aspectRatio === r && 'selected')}
                  onClick={() => handleSelect('aspectRatio', r, r)}
                >
                  <Icon className="h-6 w-6 flex-shrink-0" />
                  <span>{r}</span>
                  <span className="ml-auto text-gray-400 text-xs">{label}</span>
                </div>
              ))}
            </>
          ) : model === 'runway' || model === 'grok-imagine' ? (
            <>
              {[
                { r: '16:9', Icon: RectangleHorizontal, label: 'Widescreen' },
                { r: '9:16', Icon: RectangleVertical, label: 'Story / Reel' },
                { r: '1:1', Icon: Square, label: 'Square' },
              ].map(({ r, Icon, label }) => (
                <div
                  key={r}
                  className={cn('option', aspectRatio === r && 'selected')}
                  onClick={() => handleSelect('aspectRatio', r, r)}
                >
                  <Icon className="h-6 w-6 flex-shrink-0" />
                  <span>{r}</span>
                  <span className="ml-auto text-gray-400 text-xs">{label}</span>
                </div>
              ))}

              {model === 'runway' &&
                [
                  { r: '4:3', Icon: RectangleHorizontal, label: 'Standard' },
                  { r: '3:4', Icon: RectangleVertical, label: 'Classic Portrait' },
                ].map(({ r, Icon, label }) => (
                  <div key={r} className="option" onClick={() => handleSelect('aspectRatio', r, r)}>
                    <Icon className="h-6 w-6 flex-shrink-0" />
                    <span>{r}</span>
                    <span className="ml-auto text-gray-400 text-xs">{label}</span>
                  </div>
                ))}

              {model === 'grok-imagine' &&
                [
                  { r: '3:2', Icon: RectangleHorizontal, label: 'Landscape' },
                  { r: '2:3', Icon: RectangleVertical, label: 'Portrait' },
                ].map(({ r, Icon, label }) => (
                  <div
                    key={r}
                    className={cn('option', aspectRatio === r && 'selected')}
                    onClick={() => handleSelect('aspectRatio', r, r)}
                  >
                    <Icon className="h-6 w-6 flex-shrink-0" />
                    <span>{r}</span>
                    <span className="ml-auto text-gray-400 text-xs">{label}</span>
                  </div>
                ))}
            </>
          ) : (
            <>
              <div
                className={cn(
                  'option !items-start !flex-col !gap-1 py-2',
                  aspectRatio === '9:16' && 'selected'
                )}
                onClick={() => handleSelect('aspectRatio', '9:16', '9:16')}
              >
                <div className="flex items-center gap-2">
                  <RectangleVertical className="h-6 w-6" />
                  9:16
                </div>
                <span className="pl-5 text-gray-400 text-xs">Instagram Reels, TikTok</span>
              </div>
              <div
                className={cn(
                  'option !items-start !flex-col !gap-1 py-2',
                  aspectRatio === '16:9' && 'selected'
                )}
                onClick={() => handleSelect('aspectRatio', '16:9', '16:9')}
              >
                <div className="flex items-center gap-2">
                  <RectangleHorizontal className="h-6 w-6" />
                  16:9
                </div>
                <span className="pl-5 text-gray-400 text-xs">YouTube</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
